// src/pages/admin/reports/DataExport.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import DatePicker from "../../../components/common/DatePicker";
import SingleSelectSearch from "../../../components/common/SingleSelectSearch";
import API from "../../../api";
import { listAllActiveStudents } from "../../../api/users";
import {
    firstDayOfMonthRegional,
    todayRegionalDate,
    toISODateFromRegional,
    todayIso,
} from "../../../utils/date";
import { userProfilePath, roleAreaPrefix } from "../../../utils/userRoutes";
import { getActiveRole } from "../../../utils/auth";
import { userDisplayName } from '../../../utils/displayNames';
import i18n from '../../../i18n';

function getFirstDayOfMonth() {
    return firstDayOfMonthRegional();
}
function getToday() {
    return todayRegionalDate();
}
function ddmmyyyyToIso(str) {
    const iso = toISODateFromRegional(str);
    return iso || null;
}

async function downloadCsv(url, filename) {
    const res = await API.get(url, { responseType: "blob" });
    // API errors often return JSON with blob responseType — detect that
    const contentType = res.headers?.["content-type"] || "";
    if (contentType.includes("application/json")) {
        const text = await res.data.text?.() ?? await new Response(res.data).text();
        let msg = i18n.t('common.exportFailed', { defaultValue: 'Export failed' });
        try {
            msg = JSON.parse(text)?.message || msg;
        } catch {
            /* ignore */
        }
        throw new Error(msg);
    }
    const blob = new Blob([res.data], { type: "text/csv;charset=utf-8;" });
    const href = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = href;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(href);
}

function ExportCard({ icon, title, description, action, highlight = false }) {
    return (
        <div className={`rounded-2xl border bg-white p-6 shadow-sm flex flex-col gap-4 ${
            highlight ? "border-sky-300 ring-2 ring-sky-100" : "border-gray-200"
        }`}>
            <div className="flex items-start gap-4">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600 text-xl">
                    {icon}
                </div>
                <div>
                    <h3 className="font-semibold text-gray-900">{title}</h3>
                    <p className="mt-1 text-sm text-gray-500 leading-relaxed">{description}</p>
                </div>
            </div>
            {action}
        </div>
    );
}

function DownloadButton({ onClick, loading, children, preparingLabel, disabled = false }) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={loading || disabled}
            className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50 self-start"
        >
            {loading ? (
                <>
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    {preparingLabel}
                </>
            ) : (
                <>
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                            d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    {children}
                </>
            )}
        </button>
    );
}

function studentLabel(s) {
    const nameParts = [s.firstName, s.middleName, s.lastName].filter(Boolean);
    const id = s.userId || s.id || s.studentId;
    const fullName = s.fullName || s.name || nameParts.join(" ") || s.username
        || i18n.t('attendance.studentFallback', { id, defaultValue: `Student #${id}` });
    return { value: String(id), label: `${fullName} (#${id})` };
}

export default function DataExport() {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const [txnFrom, setTxnFrom] = useState(getFirstDayOfMonth());
    const [txnTo, setTxnTo] = useState(getToday());
    const [studentOptions, setStudentOptions] = useState([]);
    const [studentsLoading, setStudentsLoading] = useState(true);
    const [studentsLoadError, setStudentsLoadError] = useState("");
    /** Wallet-tx export only: '' = none selected */
    const [walletStudentId, setWalletStudentId] = useState(() => searchParams.get("userId") || "");

    const [loadingStudents, setLoadingStudents] = useState(false);
    const [loadingTxns, setLoadingTxns] = useState(false);
    const [loadingGuardians, setLoadingGuardians] = useState(false);
    const [loadingPlaying, setLoadingPlaying] = useState(false);
    const [error, setError] = useState("");
    /** True only when DSAR requester is a student (wallet top-up applies). */
    const [dsarIsStudent, setDsarIsStudent] = useState(false);

    const dsarContext = useMemo(() => {
        const userId = searchParams.get("userId");
        if (!userId) return null;
        return {
            userId,
            name: searchParams.get("name") || "",
            username: searchParams.get("username") || "",
            role: (searchParams.get("role") || "").toUpperCase(),
        };
    }, [searchParams]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setStudentsLoading(true);
            setStudentsLoadError("");
            setDsarIsStudent(false);
            try {
                const arr = await listAllActiveStudents();
                const opts = (arr || [])
                    .map(studentLabel)
                    .filter((o) => o.value && o.value !== "undefined" && o.value !== "null");
                if (cancelled) return;
                setStudentOptions(opts);

                const deepId = searchParams.get("userId");
                const roleHint = (searchParams.get("role") || "").toUpperCase();
                if (roleHint === "STUDENT" || (deepId && opts.some((o) => o.value === String(deepId)))) {
                    setDsarIsStudent(true);
                }
                if (deepId && !opts.some((o) => o.value === String(deepId))) {
                    try {
                        const { data: u } = await API.get(`/admin/users/${deepId}`);
                        if (!cancelled) {
                            const roles = (u?.roles || []).map((r) => String(r).toUpperCase());
                            if (roles.includes("STUDENT") || roleHint === "STUDENT") {
                                setDsarIsStudent(true);
                            }
                            setStudentOptions((prev) => {
                                if (prev.some((o) => o.value === String(deepId))) return prev;
                                return [...prev, studentLabel(u)];
                            });
                        }
                    } catch {
                        if (!cancelled) {
                            if (roleHint === "STUDENT") setDsarIsStudent(true);
                            setStudentOptions((prev) => {
                                if (prev.some((o) => o.value === String(deepId))) return prev;
                                const name = searchParams.get("name") || userDisplayName(null, deepId, t);
                                return [...prev, { value: String(deepId), label: `${name} (#${deepId})` }];
                            });
                        }
                    }
                }
                if (!cancelled && opts.length === 0 && !deepId) {
                    setStudentsLoadError(t("reports.dataExport.studentsEmpty"));
                }
            } catch {
                if (!cancelled) {
                    setStudentOptions([]);
                    setStudentsLoadError(t("reports.dataExport.studentsLoadFailed"));
                }
            } finally {
                if (!cancelled) setStudentsLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [searchParams, t]);

    useEffect(() => {
        const fromUrl = searchParams.get("userId") || "";
        if (fromUrl && dsarIsStudent) setWalletStudentId(fromUrl);
    }, [searchParams, dsarIsStudent]);

    const walletStudent = useMemo(
        () => studentOptions.find((o) => o.value === String(walletStudentId)) || null,
        [studentOptions, walletStudentId]
    );

    const profilePath = dsarContext ? userProfilePath(dsarContext.userId) : null;
    const topupPath = dsarContext && dsarIsStudent
        ? `${roleAreaPrefix(getActiveRole())}/advance-payment?studentId=${dsarContext.userId}`
        : null;

    const todayLabel = () => todayIso();

    const handleStudentsExport = async () => {
        setError("");
        setLoadingStudents(true);
        try {
            // DSAR deep-link: scope roster to that requester only
            const params = new URLSearchParams();
            if (dsarContext?.userId) params.set("studentId", dsarContext.userId);
            const qs = params.toString();
            const url = qs ? `/admin/export/students?${qs}` : "/admin/export/students";
            const filename = dsarContext?.userId
                ? `student-${dsarContext.userId}-${todayLabel()}.csv`
                : `students-${todayLabel()}.csv`;
            await downloadCsv(url, filename);
        } catch {
            setError(t("reports.dataExport.exportStudentsFailed"));
        } finally {
            setLoadingStudents(false);
        }
    };

    const handleGuardiansExport = async () => {
        setError("");
        setLoadingGuardians(true);
        try {
            const params = new URLSearchParams();
            if (dsarContext?.userId) params.set("studentId", dsarContext.userId);
            const qs = params.toString();
            const url = qs ? `/admin/export/guardians?${qs}` : "/admin/export/guardians";
            const filename = dsarContext?.userId
                ? `guardians-student-${dsarContext.userId}-${todayLabel()}.csv`
                : `guardians-${todayLabel()}.csv`;
            await downloadCsv(url, filename);
        } catch {
            setError(t("reports.dataExport.exportGuardiansFailed"));
        } finally {
            setLoadingGuardians(false);
        }
    };

    const handlePlayingExport = async () => {
        const studentId = dsarContext?.userId || walletStudentId;
        if (!studentId) {
            setError(t("reports.dataExport.playingStudentRequired"));
            return;
        }
        setError("");
        setLoadingPlaying(true);
        try {
            const url = `/admin/export/playing-games?studentId=${encodeURIComponent(studentId)}`;
            await downloadCsv(url, `playing-games-user-${studentId}-${todayLabel()}.csv`);
        } catch {
            setError(t("reports.dataExport.exportPlayingFailed"));
        } finally {
            setLoadingPlaying(false);
        }
    };

    const handleTxnsExport = async () => {
        const fromIso = ddmmyyyyToIso(txnFrom);
        const toIso = ddmmyyyyToIso(txnTo);
        if (!fromIso || !toIso) {
            setError(t("reports.dataExport.invalidDateRange"));
            return;
        }
        if (!walletStudentId) {
            setError(t("reports.dataExport.studentRequiredForTxns"));
            return;
        }
        setError("");
        setLoadingTxns(true);
        try {
            const params = new URLSearchParams({
                from: fromIso,
                to: toIso,
                studentId: String(walletStudentId),
            });
            await downloadCsv(
                `/admin/export/wallet-transactions?${params}`,
                `wallet-transactions-student-${walletStudentId}-${fromIso}-to-${toIso}.csv`
            );
        } catch {
            setError(t("reports.dataExport.exportTxnsFailed"));
        } finally {
            setLoadingTxns(false);
        }
    };

    return (
        <div className="p-6 space-y-6 max-w-3xl">
            <div>
                <h2 className="text-xl font-bold text-gray-900">{t("reports.dataExport.title")}</h2>
                <p className="mt-1 text-sm text-gray-500">
                    {t("reports.dataExport.subtitle")}
                </p>
            </div>

            {dsarContext && (
                <div className="rounded-2xl border border-sky-200 bg-sky-50 px-5 py-4 text-sky-950">
                    <p className="text-sm font-semibold">
                        {t("reports.dataExport.dsarBannerTitle")}
                    </p>
                    <p className="mt-1 text-sm leading-relaxed text-sky-900/90">
                        {t("reports.dataExport.dsarBannerBody", {
                            name: dsarContext.name || t("reports.dataExport.dsarUnknownName"),
                            userId: dsarContext.userId,
                            username: dsarContext.username || "—",
                        })}
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2">
                        {profilePath && (
                            <Link
                                to={profilePath}
                                className="inline-flex items-center rounded-lg bg-white px-3 py-1.5 text-xs font-semibold text-sky-800 shadow-sm ring-1 ring-sky-200 hover:bg-sky-100"
                            >
                                {t("reports.dataExport.dsarOpenProfile")}
                            </Link>
                        )}
                        {topupPath && (
                            <Link
                                to={topupPath}
                                className="inline-flex items-center rounded-lg bg-white px-3 py-1.5 text-xs font-semibold text-sky-800 shadow-sm ring-1 ring-sky-200 hover:bg-sky-100"
                            >
                                {t("reports.dataExport.dsarOpenWallet")}
                            </Link>
                        )}
                    </div>
                </div>
            )}

            {error && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    {error}
                </div>
            )}

            <div className="space-y-4">
                <ExportCard
                    icon="👥"
                    highlight={Boolean(dsarContext)}
                    title={t("reports.dataExport.studentRoster")}
                    description={dsarContext
                        ? t("reports.dataExport.studentRosterDescOne")
                        : t("reports.dataExport.studentRosterDesc")}
                    action={
                        <DownloadButton onClick={handleStudentsExport} loading={loadingStudents} preparingLabel={t("reports.dataExport.preparing")}>
                            {dsarContext
                                ? t("reports.dataExport.downloadOneStudent")
                                : t("reports.dataExport.downloadStudents")}
                        </DownloadButton>
                    }
                />

                <ExportCard
                    icon="👨‍👩‍👧"
                    highlight={Boolean(dsarContext)}
                    title={t("reports.dataExport.guardianLinks")}
                    description={dsarContext
                        ? t("reports.dataExport.guardianLinksDescOne")
                        : t("reports.dataExport.guardianLinksDesc")}
                    action={
                        <DownloadButton onClick={handleGuardiansExport} loading={loadingGuardians} preparingLabel={t("reports.dataExport.preparing")}>
                            {dsarContext
                                ? t("reports.dataExport.downloadOneGuardians")
                                : t("reports.dataExport.downloadGuardians")}
                        </DownloadButton>
                    }
                />

                <ExportCard
                    icon="💳"
                    highlight={Boolean(walletStudentId)}
                    title={t("reports.dataExport.walletTransactions")}
                    description={t("reports.dataExport.walletTransactionsDescOne")}
                    action={
                        <div className="flex flex-col gap-3 w-full">
                            <div>
                                <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                                    {t("reports.dataExport.walletStudentFilter")}
                                </label>
                                <SingleSelectSearch
                                    options={studentOptions}
                                    value={walletStudentId}
                                    onChange={(v) => setWalletStudentId(v == null ? "" : String(v))}
                                    placeholder={t("reports.dataExport.chooseStudent")}
                                    loading={studentsLoading}
                                    loadError={!!studentsLoadError}
                                    className="max-w-xl"
                                />
                                {studentsLoadError ? (
                                    <p className="mt-1.5 text-xs font-medium text-red-600">{studentsLoadError}</p>
                                ) : null}
                                {!studentsLoadError && walletStudent ? (
                                    <p className="mt-1.5 text-xs text-slate-500">
                                        {t("reports.dataExport.walletExportingFor", { student: walletStudent.label })}
                                    </p>
                                ) : null}
                            </div>
                            <div className="flex flex-wrap items-center gap-3">
                                <div className="flex items-center gap-2">
                                    <label className="text-xs font-medium text-gray-500 whitespace-nowrap">{t("reports.dataExport.from")}</label>
                                    <DatePicker value={txnFrom} onChange={setTxnFrom} />
                                </div>
                                <div className="flex items-center gap-2">
                                    <label className="text-xs font-medium text-gray-500 whitespace-nowrap">{t("reports.dataExport.to")}</label>
                                    <DatePicker value={txnTo} onChange={setTxnTo} />
                                </div>
                            </div>
                            <DownloadButton
                                onClick={handleTxnsExport}
                                loading={loadingTxns}
                                preparingLabel={t("reports.dataExport.preparing")}
                                disabled={!walletStudentId}
                            >
                                {t("reports.dataExport.downloadOneTransactions")}
                            </DownloadButton>
                        </div>
                    }
                />
                <ExportCard
                    icon="♟️"
                    highlight={Boolean(dsarContext || walletStudentId)}
                    title={t("reports.dataExport.playingGames")}
                    description={t("reports.dataExport.playingGamesDesc")}
                    action={
                        <div className="flex flex-col gap-3 w-full">
                            {!dsarContext && (
                                <div>
                                    <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                                        {t("reports.dataExport.walletStudentFilter")}
                                    </label>
                                    <SingleSelectSearch
                                        options={studentOptions}
                                        value={walletStudentId}
                                        onChange={(v) => setWalletStudentId(v == null ? "" : String(v))}
                                        placeholder={t("reports.dataExport.chooseStudent")}
                                        loading={studentsLoading}
                                        loadError={!!studentsLoadError}
                                        className="max-w-xl"
                                    />
                                    {studentsLoadError ? (
                                        <p className="mt-1.5 text-xs font-medium text-red-600">{studentsLoadError}</p>
                                    ) : null}
                                </div>
                            )}
                            <DownloadButton
                                onClick={handlePlayingExport}
                                loading={loadingPlaying}
                                preparingLabel={t("reports.dataExport.preparing")}
                                disabled={!dsarContext?.userId && !walletStudentId}
                            >
                                {t("reports.dataExport.downloadPlaying")}
                            </DownloadButton>
                        </div>
                    }
                />
            </div>

            <p className="text-xs text-gray-400">
                ℹ️ {t("reports.dataExport.footer")}
            </p>
        </div>
    );
}
